import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';

// Maximum locations per day per user (from 27.3-CONTEXT.md)
const MAX_LOCATIONS = 3;

export default function ShiftForm({ date, editingShift, onSave, onCancel }) {
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [locations, setLocations] = useState([{
    location_id: '',
    location_name: '',
    location_type: 'gym',
    start_time: '09:00',
    end_time: '18:00'
  }]);
  const [notes, setNotes] = useState('');
  const [gymList, setGymList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeamMembers();
    fetchGyms();
  }, []);

  // Pre-populate form when editing
  useEffect(() => {
    if (editingShift && teamMembers.length > 0) {
      const user = teamMembers.find(u => u.id === editingShift.user_id);
      setSelectedUser(user || { id: editingShift.user_id, name: editingShift.user_name, role: editingShift.user_role });
      setLocations(editingShift.locations || []);
      setNotes(editingShift.notes || '');
    }
  }, [editingShift, teamMembers]);

  const fetchTeamMembers = async () => {
    try {
      const response = await axiosInstance.get('/team-members');
      setTeamMembers(response.data);
    } catch (error) {
      toast.error('Failed to load team members');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGyms = async () => {
    try {
      const response = await axiosInstance.get('/gyms');
      setGymList(response.data);
    } catch (error) {
      toast.error('Failed to load gyms');
      console.error(error);
    }
  };

  const addLocation = () => {
    setLocations([...locations, {
      location_id: '',
      location_name: '',
      location_type: 'gym',
      start_time: '09:00',
      end_time: '18:00'
    }]);
  };

  const removeLocation = (index) => {
    setLocations(locations.filter((_, i) => i !== index));
  };

  const updateLocation = (index, field, value) => {
    const updated = [...locations];
    updated[index][field] = value;

    // Auto-populate location_name when location_id changes
    if (field === 'location_id' && value) {
      if (updated[index].location_type === 'gym') {
        const gym = gymList.find(g => g.gym_id === value);
        if (gym) {
          updated[index].location_name = gym.name;
        }
      } else if (updated[index].location_type === 'hq') {
        updated[index].location_name = 'HQ Office';
      }
    }

    setLocations(updated);
  };

  const handleSave = () => {
    // Validation
    if (!selectedUser) {
      toast.error('Please select a team member');
      return;
    }

    if (locations.some(loc => !loc.location_id || !loc.start_time || !loc.end_time)) {
      toast.error('Please fill all location details');
      return;
    }

    const shift = {
      shift_id: editingShift?.shift_id || Math.random().toString(36).substr(2, 9), // Keep ID when editing
      user_id: selectedUser.id,
      user_name: selectedUser.name,
      user_role: selectedUser.role,
      date: date, // YYYY-MM-DD from parent
      locations: locations,
      notes: notes
    };

    onSave(shift);
  };

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div className="space-y-4 p-4 border rounded-lg">
      <h3 className="font-semibold">{editingShift ? 'Edit' : 'Add'} Shift for {date}</h3>

      <div>
        <Label>Team Member</Label>
        <Select
          value={selectedUser?.id || ''}
          onValueChange={(value) => {
            const user = teamMembers.find(u => u.id === value);
            setSelectedUser(user);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select team member" />
          </SelectTrigger>
          <SelectContent>
            {teamMembers.map(member => (
              <SelectItem key={member.id} value={member.id}>
                {member.name} ({member.role})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {locations.map((location, index) => (
        <div key={index} className="space-y-2 border-l-2 pl-4">
          <div className="flex items-center justify-between">
            <Label>Location {index + 1}</Label>
            {locations.length > 1 && (
              <Button variant="ghost" size="sm" onClick={() => removeLocation(index)}>Remove</Button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Type</Label>
              <Select value={location.location_type} onValueChange={(value) => updateLocation(index, 'location_type', value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gym">Gym</SelectItem>
                  <SelectItem value="hq">HQ</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Location</Label>
              <Select value={location.location_id} onValueChange={(value) => updateLocation(index, 'location_id', value)}>
                <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>
                  {location.location_type === 'gym' ? (
                    gymList.map(gym => (
                      <SelectItem key={gym.gym_id} value={gym.gym_id}>{gym.name}</SelectItem>
                    ))
                  ) : (
                    <SelectItem value="hq">HQ Office</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Start Time</Label>
              <Input type="time" value={location.start_time} onChange={(e) => updateLocation(index, 'start_time', e.target.value)} />
            </div>

            <div>
              <Label>End Time</Label>
              <Input type="time" value={location.end_time} onChange={(e) => updateLocation(index, 'end_time', e.target.value)} />
            </div>
          </div>
        </div>
      ))}

      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={addLocation}
          disabled={locations.length >= MAX_LOCATIONS}
          title={locations.length >= MAX_LOCATIONS ? `Maximum ${MAX_LOCATIONS} locations per day` : 'Add another location'}
        >
          Add Location (Split Shift)
        </Button>
        <span className="text-sm text-gray-500">
          {locations.length}/{MAX_LOCATIONS} locations
        </span>
      </div>
      {locations.length >= MAX_LOCATIONS && (
        <p className="text-sm text-amber-600">
          Maximum {MAX_LOCATIONS} locations per day allowed per person.
        </p>
      )}

      <div>
        <Label>Notes (Optional)</Label>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any special notes..." />
      </div>

      <div className="flex gap-2">
        <Button onClick={handleSave}>{editingShift ? 'Update Shift' : 'Save Shift'}</Button>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
